import React, { useState, useEffect } from "react";

const App: React.FC = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Add SEO meta tags
  useEffect(() => {
    // Set document title
    document.title = "Geet Kashyap - Full Stack Web Developer Portfolio";

    // Add meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Geet Kashyap - Experienced Full Stack Web Developer specializing in React, Node.js, and modern web technologies. View my portfolio of innovative web applications."
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Geet Kashyap - Experienced Full Stack Web Developer specializing in React, Node.js, and modern web technologies. View my portfolio of innovative web applications.";
      document.getElementsByTagName("head")[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.createElement("meta");
    metaKeywords.name = "keywords";
    metaKeywords.content =
      "web developer, full stack developer, React developer, Node.js, JavaScript, TypeScript, portfolio, Geet Kashyap";
    document.getElementsByTagName("head")[0].appendChild(metaKeywords);

    // Add viewport meta tag
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (!metaViewport) {
      const viewport = document.createElement("meta");
      viewport.name = "viewport";
      viewport.content = "width=device-width, initial-scale=1.0";
      document.getElementsByTagName("head")[0].appendChild(viewport);
    }

    // Add Open Graph tags
    const ogTags = [
      {
        property: "og:title",
        content: "Geet Kashyap - Full Stack Web Developer",
      },
      {
        property: "og:description",
        content:
          "Experienced Full Stack Web Developer specializing in React, Node.js, and modern web technologies.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: window.location.href },
    ];

    ogTags.forEach((tag) => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", tag.property);
      meta.content = tag.content;
      document.getElementsByTagName("head")[0].appendChild(meta);
    });
  }, []);

  const projects = [
    {
      title: "E-commerce Dashboard",
      description:
        "A comprehensive dashboard for e-commerce management with analytics and inventory tracking.",
      link: "https://ecommercewebsitedashboard.vercel.app/",
      tech: ["React", "TypeScript", "Chart.js"],
    },
    {
      title: "Digital Data Analytics",
      description:
        "Advanced data analytics platform with interactive visualizations and reporting tools.",
      link: "https://digitaldataanalytics.vercel.app/",
      tech: ["React", "D3.js", "Node.js"],
    },
    {
      title: "Tech Mart",
      description:
        "Modern e-commerce platform for technology products with seamless user experience.",
      link: "https://tech-mart-ivory.vercel.app/",
      tech: ["Next.js", "Tailwind CSS", "MongoDB"],
    },
    {
      title: "MoodTunes",
      description:
        "Music streaming application that curates playlists based on user's mood and preferences.",
      link: "https://moodtunes-seven.vercel.app/",
      tech: ["React", "Spotify API", "Machine Learning"],
    },
    {
      title: "Engagement Automation Hub",
      description:
        "Automated engagement platform for social media management and customer interaction.",
      link: "https://engagement-automation-hub.vercel.app/",
      tech: ["React", "Express.js", "WebSocket"],
    },
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "about", "projects", "contact"];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App">
      <style>{`
        /* Previous CSS remains the same */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #e0e0e0;
          background: #0a0a0a;
        }

        .App {
          min-height: 100vh;
        }

        /* Navigation */
        .nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(10, 10, 10, 0.95);
          backdrop-filter: blur(10px);
          z-index: 1000;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo-text {
          font-size: 1.5rem;
          font-weight: bold;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .nav-menu {
          display: flex;
          gap: 2rem;
        }

        .nav-link {
          background: none;
          border: none;
          color: #e0e0e0;
          cursor: pointer;
          font-size: 1rem;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          text-transform: capitalize;
        }

        .nav-link:hover,
        .nav-link.active {
          color: #667eea;
          background: rgba(102, 126, 234, 0.1);
        }

        .hamburger {
          display: none;
          flex-direction: column;
          cursor: pointer;
          gap: 3px;
        }

        .hamburger span {
          width: 25px;
          height: 3px;
          background: #e0e0e0;
          transition: 0.3s;
          border-radius: 2px;
        }

        /* Hero Section */
        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          position: relative;
          background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 50%);
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 1rem;
        }

        .highlight {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.5rem;
          color: #a0a0a0;
          margin-bottom: 1.5rem;
          font-weight: 300;
        }

        .hero-description {
          font-size: 1.1rem;
          color: #c0c0c0;
          margin-bottom: 2.5rem;
          line-height: 1.6;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
        }

        .btn {
          padding: 1rem 2rem;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
          text-decoration: none;
          display: inline-block;
          text-align: center;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
        }

        .btn-secondary {
          background: transparent;
          color: #667eea;
          border: 2px solid #667eea;
        }

        .btn-secondary:hover {
          background: #667eea;
          color: white;
        }

        .floating-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 2rem;
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .code-snippet {
          font-family: 'Courier New', monospace;
          font-size: 0.9rem;
        }

        .code-line {
          margin: 0.5rem 0;
        }

        .code-indent {
          padding-left: 1.5rem;
        }

        .code-keyword { color: #ff6b6b; }
        .code-variable { color: #4ecdc4; }
        .code-function { color: #ffe66d; }
        .code-string { color: #95e1d3; }
        .code-operator { color: #f38ba8; }
        .code-brackets { color: #cba6f7; }

        .hero-scroll {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
        }

        .scroll-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          color: #a0a0a0;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .scroll-indicator:hover {
          color: #667eea;
        }

        .scroll-arrow {
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }

        /* Sections */
        section {
          padding: 5rem 0;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-title {
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 3rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* About Section */
        .about {
          background: #0f0f0f;
        }

        .about-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
        }

        .about-content p {
          font-size: 1.1rem;
          margin-bottom: 2rem;
          color: #c0c0c0;
          line-height: 1.8;
        }

        .skills-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
          text-align: left;
        }

        .skill-category h3 {
          color: #667eea;
          margin-bottom: 1rem;
          font-size: 1.2rem;
        }

        .skill-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .skill-tag {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        /* Projects Section */
        .projects {
          background: #0a0a0a;
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        .project-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 2rem;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .project-card:hover {
          transform: translateY(-5px);
          border-color: rgba(102, 126, 234, 0.5);
        }

        .project-title {
          color: #667eea;
          font-size: 1.3rem;
          margin-bottom: 1rem;
        }

        .project-description {
          color: #c0c0c0;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .project-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .tech-tag {
          background: rgba(102, 126, 234, 0.2);
          color: #667eea;
          padding: 0.3rem 0.8rem;
          border-radius: 15px;
          font-size: 0.8rem;
        }

        .project-link {
          color: #667eea;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.3s ease;
        }

        .project-link:hover {
          color: #764ba2;
        }

        /* Contact Section */
        .contact {
          background: #0f0f0f;
        }

        .contact-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
        }

        .contact-info h3 {
          color: #667eea;
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .contact-info p {
          color: #c0c0c0;
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .contact-icon {
          font-size: 1.5rem;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 50%;
        }

        .contact-item h4 {
          color: #667eea;
          margin-bottom: 0.5rem;
        }

        .contact-item a {
          color: #c0c0c0;
          text-decoration: none;
        }

        .contact-item a:hover {
          color: #667eea;
        }

        .contact-form {
          background: rgba(255, 255, 255, 0.05);
          padding: 2rem;
          border-radius: 15px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #e0e0e0;
          font-size: 1rem;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #a0a0a0;
        }

        /* Footer */
        .footer {
          background: #0a0a0a;
          padding: 2rem 0;
          text-align: center;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .footer p {
          color: #a0a0a0;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hamburger {
            display: flex;
          }

          .nav-menu {
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            flex-direction: column;
            padding: 1rem;
            transform: translateY(-100%);
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
          }

          .nav-menu.active {
            transform: translateY(0);
            opacity: 1;
            visibility: visible;
          }

          .hero-content {
            grid-template-columns: 1fr;
            text-align: center;
            gap: 2rem;
          }

          .hero-title {
            font-size: 2.5rem;
          }

          .hero-buttons {
            justify-content: center;
            flex-wrap: wrap;
          }

          .contact-content {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .skills-grid {
            grid-template-columns: 1fr;
          }

          .projects-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 1rem;
          }

          .hero-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 2rem;
          }

          .nav-container {
            padding: 1rem;
          }
        }
      `}</style>

      {/* Navigation with semantic HTML */}
      <nav className="nav" role="navigation" aria-label="Main navigation">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-text">Geet Kashyap</span>
          </div>

          <div className={`nav-menu ${isMenuOpen ? "active" : ""}`}>
            {["home", "about", "projects", "contact"].map((item) => (
              <button
                key={item}
                className={`nav-link ${activeSection === item ? "active" : ""}`}
                onClick={() => scrollToSection(item)}
                aria-label={`Navigate to ${item} section`}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
          </div>

          <button
            className="hamburger"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Home Section with semantic HTML */}
      <section id="home" className="hero" role="banner">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Hi, I'm <span className="highlight">Geet Kashyap</span>
            </h1>
            <h2 className="hero-subtitle">Full Stack Web Developer</h2>
            <p className="hero-description">
              Passionate about creating innovative web solutions that deliver
              exceptional user experiences. Specialized in modern JavaScript
              frameworks and responsive design.
            </p>
            <div className="hero-buttons">
              <button
                className="btn btn-primary"
                onClick={() => scrollToSection("projects")}
              >
                View My Work
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => scrollToSection("contact")}
              >
                Get In Touch
              </button>
            </div>
          </div>
          <aside className="hero-visual" aria-label="Code example">
            <div className="floating-card">
              <div
                className="code-snippet"
                role="img"
                aria-label="Sample code showing developer passion"
              >
                <div className="code-line">
                  <span className="code-keyword">const</span>
                  <span className="code-variable"> developer</span>
                  <span className="code-operator"> = </span>
                  <span className="code-string">"passionate"</span>
                </div>
                <div className="code-line">
                  <span className="code-keyword">function</span>
                  <span className="code-function"> createAmazingApps</span>
                  <span className="code-brackets">()</span>
                  <span className="code-brackets">{"{"}</span>
                </div>
                <div className="code-line code-indent">
                  <span className="code-keyword">return</span>
                  <span className="code-string"> "innovation"</span>
                </div>
                <div className="code-line">
                  <span className="code-brackets">{"}"}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
        <div className="hero-scroll">
          <button
            className="scroll-indicator"
            onClick={() => scrollToSection("about")}
            aria-label="Scroll to about section"
          >
            <span>Scroll Down</span>
            <div className="scroll-arrow">↓</div>
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <h2 className="section-title">About Me</h2>
          <article className="about-content">
            <div className="about-text">
              <p>
                I'm a passionate Full Stack Web Developer with a strong
                foundation in modern web technologies. I love transforming ideas
                into digital reality through clean, efficient code and stunning
                user interfaces.
              </p>
              <p>
                My expertise spans across front-end frameworks like React and
                Vue.js, back-end technologies including Node.js and Express, and
                database management with MongoDB and PostgreSQL.
              </p>

              <div className="skills-grid">
                <div className="skill-category">
                  <h3>Frontend</h3>
                  <div className="skill-tags">
                    <span className="skill-tag">React</span>
                    <span className="skill-tag">TypeScript</span>
                    <span className="skill-tag">Next.js</span>
                    <span className="skill-tag">Vue.js</span>
                    <span className="skill-tag">Tailwind CSS</span>
                  </div>
                </div>
                <div className="skill-category">
                  <h3>Backend</h3>
                  <div className="skill-tags">
                    <span className="skill-tag">Node.js</span>
                    <span className="skill-tag">Express.js</span>
                    <span className="skill-tag">MongoDB</span>
                    <span className="skill-tag">PostgreSQL</span>
                    <span className="skill-tag">REST API</span>
                  </div>
                </div>
                <div className="skill-category">
                  <h3>Tools & Others</h3>
                  <div className="skill-tags">
                    <span className="skill-tag">Git</span>
                    <span className="skill-tag">Docker</span>
                    <span className="skill-tag">AWS</span>
                    <span className="skill-tag">Vercel</span>
                    <span className="skill-tag">Figma</span>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="projects">
        <div className="container">
          <h2 className="section-title">Featured Projects</h2>
          <div className="projects-grid" role="list">
            {projects.map((project, index) => (
              <article key={index} className="project-card" role="listitem">
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  <div className="project-tech" aria-label="Technologies used">
                    {project.tech.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-link"
                    aria-label={`View ${project.title} project in new tab`}
                  >
                    View Project →
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title">Let's Work Together</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p>
                I'm always interested in new opportunities and exciting
                projects. Let's discuss how we can bring your ideas to life!
              </p>

              <address className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon" aria-hidden="true">
                    📧
                  </div>
                  <div>
                    <h4>Email</h4>
                    <a href="mailto:geetkashyap931@gmail.com">
                      geetkashyap931@gmail.com
                    </a>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon" aria-hidden="true">
                    📱
                  </div>
                  <div>
                    <h4>Phone</h4>
                    <a href="tel:7099427236">+91 7099427236</a>
                  </div>
                </div>
              </address>
            </div>

            <form className="contact-form" aria-label="Contact form">
              <div className="form-group">
                <label htmlFor="name" className="sr-only">
                  Your Name
                </label>
                <input type="text" id="name" placeholder="Your Name" required />
              </div>
              <div className="form-group">
                <label htmlFor="email" className="sr-only">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="Your Email"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="subject" className="sr-only">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  placeholder="Subject"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="message" className="sr-only">
                  Your Message
                </label>
                <textarea
                  id="message"
                  placeholder="Your Message"
                  rows={5}
                  required
                ></textarea>
              </div>
              <button type="submit" className="btn btn-primary">
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Geet Kashyap. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
